import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { formatEthereumAddress } from '@sorare/core/src/lib/ethereum';
import { isType } from '@sorare/core/src/lib/gql';
import { css } from '@sorare/css';

import type {
  SmallUser_anonymousUser,
  SmallUser_user,
} from './__generated__/index.graphql.ts';

type Props = {
  user: SmallUser_user | SmallUser_anonymousUser;
};

const primaryText = css`
  color: var(--c-onBase-primary);
`;
const infoContainer = css`
  max-width: calc(100% - 44px);
  white-space: nowrap;
  text-overflow: ellipsis;
  display: block;
  overflow: hidden;
`;

const SmallAnonymousUser = ({ user }: { user: SmallUser_anonymousUser }) => {
  return (
    <Horizontal gap={0.5} className={primaryText}>
      <Avatar user={user} size={2} />
      <BodyM className={infoContainer}>
        {formatEthereumAddress(user.ethereumAddress)}
      </BodyM>
    </Horizontal>
  );
};

const SmallUserBody = ({ user }: { user: SmallUser_user }) => {
  return (
    <>
      <Avatar user={user} size={2} />
      <BodyM className={infoContainer}>
        <UserName user={user} />
      </BodyM>
    </>
  );
};

const SuspendedRegularUser = ({ user }: { user: SmallUser_user }) => {
  return (
    <Horizontal as="span" gap={0.5} className={primaryText}>
      <SmallUserBody user={user} />
    </Horizontal>
  );
};

const SmallRegularUser = ({ user }: { user: SmallUser_user }) => {
  return (
    <Horizontal as={MyClubLink} user={user} gap={0.5} className={primaryText}>
      <SmallUserBody user={user} />
    </Horizontal>
  );
};

export const SmallUser = (props: Props) => {
  const { user } = props;
  if (isType(user, 'User')) {
    if (user.suspended) {
      return <SuspendedRegularUser user={user} />;
    }
    return <SmallRegularUser user={user} />;
  }
  return <SmallAnonymousUser user={user} />;
};

SmallUser.fragments = {
  user: gql`
    fragment SmallUser_user on User {
      slug
      ...Avatar_publicUserInfoInterface
      ...UserName_publicUserInfoInterface
    }
    ${Avatar.fragments.publicUserInfoInterface}
    ${UserName.fragments.user}
  ` as TypedDocumentNode<SmallUser_user>,
  anonymousUser: gql`
    fragment SmallUser_anonymousUser on AnonymousUser {
      ethereumAddress
      id
      ...Avatar_anonymousUser
    }
    ${Avatar.fragments.anonymousUser}
  ` as TypedDocumentNode<SmallUser_anonymousUser>,
};
